import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Bell,
  ChevronLeft,
  ExternalLink,
  ChevronRight,
  AlertTriangle,
  Gift,
  Package,
  Shield,
  Megaphone,
} from "lucide-react";
import { notificationService } from "../../services/notification.service";

const NotificationsBar = ({ isScrolled }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentNotification, setCurrentNotification] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map API notification types to icons
  const notificationIcons = {
    system_alert: AlertTriangle,
    promotional_offer: Gift,
    new_product: Package,
    product_restock: Package,
    security_alert: Shield,
    new_blog_post: Megaphone,
    announcement: Megaphone,
  };

  // Fetch active notifications
  useEffect(() => {
    fetchActiveNotifications();

    // Refresh every 30 seconds
    const interval = setInterval(fetchActiveNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchActiveNotifications = async () => {
    try {
      const response = await notificationService.getActiveNotifications({
        limit: 5,
        sort_by: "display_order",
        sort_order: "DESC",
      });

      // Format notifications for the bar
      const formattedNotifications = response.data
        .map((notif) => ({
          id: notif.id,
          title: notif.title,
          message: notif.message,
          bgColor: getStyleBgColor(notif.style),
          duration: 6000, // Default duration
          action: notif.button_url,
          actionText: notif.button_text || "عرض التفاصيل",
          type: notif.type,
          style: notif.style,
          expires_at: notif.expires_at,
          icon: notificationIcons[notif.type] || Bell,
        }))
        .filter((notif) => {
          // Filter out expired notifications
          if (!notif.expires_at) return true;
          const expiresAt = new Date(notif.expires_at);
          return expiresAt > new Date();
        });

      setNotifications(formattedNotifications);

      // Reset current notification if needed
      if (
        formattedNotifications.length > 0 &&
        currentNotification >= formattedNotifications.length
      ) {
        setCurrentNotification(0);
      }
    } catch (error) {
      console.error("Error fetching active notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStyleBgColor = (style) => {
    switch (style) {
      case "error":
        return "bg-error-50 dark:bg-error-900/20";
      case "warning":
        return "bg-warning-50 dark:bg-warning-900/20";
      case "success":
        return "bg-success-50 dark:bg-success-900/20";
      case "info":
        return "bg-primary-50 dark:bg-primary-900/20";
      case "promotional":
        return "bg-accent-gold/10 dark:bg-accent-gold/20";
      default:
        return "bg-gray-50 dark:bg-gray-900/20";
    }
  };

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-rotate notifications with pause functionality
  const nextNotification = useCallback(() => {
    if (notifications.length === 0) return;
    setCurrentNotification((prev) => (prev + 1) % notifications.length);
  }, [notifications.length]);

  useEffect(() => {
    if (!isVisible || isScrolled || isPaused || notifications.length === 0)
      return;

    const interval = setInterval(() => {
      nextNotification();
    }, notifications[currentNotification]?.duration || 6000);

    return () => clearInterval(interval);
  }, [
    isVisible,
    isScrolled,
    currentNotification,
    notifications,
    isPaused,
    nextNotification,
  ]);

  // Reset when scrolled
  useEffect(() => {
    if (isScrolled) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
      setIsPaused(false);
    }
  }, [isScrolled]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleNext = () => {
    if (notifications.length === 0) return;
    nextNotification();
  };

  const handlePrev = () => {
    if (notifications.length === 0) return;
    setCurrentNotification(
      (prev) => (prev - 1 + notifications.length) % notifications.length,
    );
  };

  const handleNotificationClick = (action) => {
    if (action) {
      window.open(action, "_blank");
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsPaused(false);
    }
  };

  const handleTouchStart = () => {
    if (isMobile) {
      setIsPaused(true);
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      setTimeout(() => setIsPaused(false), 1000);
    }
  };

  if (isScrolled || notifications.length === 0) return null;

  const currentNotif = notifications[currentNotification];

  if (!currentNotif) return null;

  const Icon = currentNotif.icon;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="relative hidden md:block"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Main Notification Bar */}
          <motion.div
            className={`border-b border-border-primary dark:border-border-primary cursor-pointer group ${
              isMobile ? "md:hidden" : "hidden md:block"
            }`}
            style={{
              background: "var(--bg-glass)",
              backdropFilter: "var(--backdrop-blur-lg)",
              border: "var(--glass-border)",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            whileHover={{
              background: isMobile
                ? "var(--bg-glass)"
                : "var(--gradient-glass)",
            }}
            transition={{ duration: 0.3 }}
          >
            {/* Loading state */}
            {loading ? (
              <div className="px-4 py-3 text-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Mobile View */}
                {isMobile ? (
                  <div className="relative">
                    <div
                      className="px-4 py-3"
                      onClick={() =>
                        handleNotificationClick(currentNotif.action)
                      }
                    >
                      {/* Mobile header with icon and close button */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <div
                            className="flex items-center justify-center w-8 h-8 rounded-xl relative"
                            style={{
                              background: "var(--bg-glass)",
                              backdropFilter: "var(--backdrop-blur)",
                            }}
                          >
                            <Icon className="w-4 h-4 text-primary-500" />
                            {!isPaused && (
                              <div
                                className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary-500 rounded-full"
                                style={{
                                  boxShadow: "var(--shadow-glow)",
                                }}
                              />
                            )}
                          </div>
                          <span className="text-xs font-semibold text-text-primary">
                            إشعارات
                          </span>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                          }}
                          className="p-1.5 rounded-lg hover:bg-error-50 dark:hover:bg-error-900/20 text-text-secondary"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Mobile notification message */}
                      <div className="mb-3">
                        <div className="text-sm font-medium text-text-primary text-right leading-relaxed">
                          {currentNotif.message}
                        </div>
                        {currentNotif.action && (
                          <div className="flex items-center justify-end space-x-1 space-x-reverse text-primary-500 mt-2">
                            <span className="text-xs font-medium">
                              {currentNotif.actionText}
                            </span>
                            <ExternalLink className="w-3 h-3" />
                          </div>
                        )}
                      </div>

                      {/* Mobile controls */}
                      <div className="flex items-center justify-between">
                        {/* Navigation dots */}
                        <div className="flex items-center space-x-2 space-x-reverse">
                          {notifications.map((_, index) => (
                            <button
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentNotification(index);
                              }}
                              className={`rounded-full transition-all ${
                                index === currentNotification
                                  ? "bg-primary-500 w-8"
                                  : "bg-gray-300 dark:bg-gray-600 w-2"
                              } h-2`}
                            />
                          ))}
                        </div>

                        {/* Navigation arrows */}
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrev();
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-text-secondary"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNext();
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-text-secondary"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mobile progress bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-500 via-primary-400 to-accent-gold"
                        initial={{ width: "100%" }}
                        animate={{
                          width: isPaused ? "100%" : "0%",
                        }}
                        transition={{
                          duration: isPaused
                            ? 0
                            : (currentNotif?.duration || 6000) / 1000,
                          ease: "linear",
                        }}
                        key={currentNotification}
                        style={{
                          boxShadow: "var(--shadow-glow)",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Desktop View */
                  <div className="container mx-auto px-4">
                    <div
                      className="flex items-center justify-between py-3"
                      onClick={() =>
                        handleNotificationClick(currentNotif.action)
                      }
                    >
                      {/* Left side - Notification content */}
                      <div className="flex items-center space-x-3 space-x-reverse flex-1">
                        {/* Animated Notification icon */}
                        <motion.div
                          className="flex items-center justify-center w-8 h-8 rounded-xl relative"
                          style={{
                            background: "var(--bg-glass)",
                            backdropFilter: "var(--backdrop-blur)",
                          }}
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          <motion.div
                            animate={{
                              scale: [1, 1.1, 1],
                              rotate: [0, 5, -5, 0],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              repeatDelay: 3,
                            }}
                          >
                            <Icon className="w-4 h-4 text-primary-500" />
                          </motion.div>

                          {/* Pulsing dot */}
                          {!isPaused && (
                            <motion.div
                              className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary-500 rounded-full"
                              animate={{ scale: [1, 1.5, 1] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              style={{
                                boxShadow: "var(--shadow-glow)",
                              }}
                            />
                          )}
                        </motion.div>

                        {/* Notification Body with animation */}
                        <div className="flex-1 min-w-0 relative overflow-hidden">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={currentNotification}
                              className="flex items-center space-x-3 space-x-reverse"
                              initial={{ opacity: 0, x: 60, scale: 0.95 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              exit={{ opacity: 0, x: -60, scale: 0.95 }}
                              transition={{
                                duration: 0.5,
                                ease: [0.4, 0, 0.2, 1],
                                scale: { duration: 0.3 },
                              }}
                            >
                              <div className="flex items-center space-x-3 space-x-reverse flex-1">
                                {/* <span className="text-sm font-semibold text-text-primary group-hover:text-primary-600 transition-colors duration-200">
                                  {currentNotif.title}
                                </span> */}

                                <span className="text-sm font-semibold text-text-primary group-hover:text-primary-600 transition-colors duration-200">
                                  {currentNotif.message}
                                </span>

                                {/* Action Link */}
                                {currentNotif.action && (
                                  <motion.div
                                    className="flex items-center space-x-1 space-x-reverse text-primary-500 group-hover:text-primary-600"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                  >
                                    <span className="text-xs font-medium">
                                      {currentNotif.actionText}
                                    </span>
                                    <ExternalLink className="w-3 h-3" />
                                  </motion.div>
                                )}
                              </div>
                            </motion.div>
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Right side - Controls */}
                      <div
                        className="flex items-center space-x-2 space-x-reverse"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Navigation dots */}
                        <div className="flex items-center space-x-1.5 space-x-reverse mr-4">
                          {notifications.map((_, index) => (
                            <motion.button
                              key={index}
                              onClick={() => setCurrentNotification(index)}
                              className={`relative rounded-full transition-all duration-300 ${
                                index === currentNotification
                                  ? "bg-primary-500"
                                  : "bg-gray-300 dark:bg-gray-600 hover:bg-primary-400"
                              }`}
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                              style={{
                                width:
                                  index === currentNotification
                                    ? "12px"
                                    : "6px",
                                height: "6px",
                              }}
                            >
                              {index === currentNotification && (
                                <motion.div
                                  className="absolute inset-0 rounded-full bg-primary-200"
                                  initial={{ scale: 1 }}
                                  animate={{ scale: [1, 1.3, 1] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                              )}
                            </motion.button>
                          ))}
                        </div>

                        {/* Navigation arrows */}
                        <div className="flex items-center space-x-1 space-x-reverse border-l border-border-primary dark:border-border-primary pl-4">
                          <motion.button
                            onClick={handlePrev}
                            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 text-text-secondary group/btn"
                            whileHover={{ scale: 1.1, x: -2 }}
                            whileTap={{ scale: 0.9 }}
                            style={{
                              background: "var(--bg-glass)",
                              backdropFilter: "var(--backdrop-blur)",
                            }}
                          >
                            <ChevronLeft className="w-3 h-3 transform rotate-180 group-hover/btn:scale-110 transition-transform duration-200" />
                          </motion.button>

                          <motion.button
                            onClick={handleNext}
                            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 text-text-secondary group/btn"
                            whileHover={{ scale: 1.1, x: 2 }}
                            whileTap={{ scale: 0.9 }}
                            style={{
                              background: "var(--bg-glass)",
                              backdropFilter: "var(--backdrop-blur)",
                            }}
                          >
                            <ChevronLeft className="w-3 h-3 group-hover/btn:scale-110 transition-transform duration-200" />
                          </motion.button>

                          {/* Close button */}
                          <motion.button
                            onClick={handleClose}
                            className="p-2 rounded-xl hover:bg-error-50 dark:hover:bg-error-900/20 hover:text-error-500 transition-all duration-200 text-text-secondary group/close"
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            style={{
                              background: "var(--bg-glass)",
                              backdropFilter: "var(--backdrop-blur)",
                            }}
                          >
                            <X className="w-3 h-3 group-hover/close:scale-110 transition-transform duration-200" />
                          </motion.button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop progress bar with pause state */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-500 via-primary-400 to-accent-gold"
                        initial={{ width: "100%" }}
                        animate={{
                          width: isPaused ? "100%" : "0%",
                        }}
                        transition={{
                          duration: isPaused
                            ? 0
                            : (currentNotif?.duration || 6000) / 1000,
                          ease: "linear",
                        }}
                        key={currentNotification}
                        style={{
                          boxShadow: "var(--shadow-glow)",
                        }}
                      />
                    </div>

                    {/* Hover glow effect */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(246, 161, 45, 0.05) 0%, rgba(251, 191, 36, 0.02) 100%)",
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationsBar;
